<script setup lang="ts">
//#region import
import { ref, computed } from 'vue';

import { cloneDeep } from 'lodash-es';

import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { typeGuard, type EmitsToListeners, type ErrorResponse, type FormErrors, type ToErrorMap } from '@/types/core';
import type { PutControlTowerCountriesTagsByIdData } from '@/types/openapi-ts';
//#endregion

//#region types
interface Props {
  popupData?: PopupData,
  isEdit?: boolean,
}

interface  Emits {
  'close': [],
  'success': []
}

export interface AddCountryGroupParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

interface PopupData {
  country_code: string[],
  name: string,
  id: string,
}

type PutControlTowerCountriesTagsByIdErrors = ToErrorMap<PutControlTowerCountriesTagsByIdData['body']>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  popupData: () => {
    return {
      country_code: [],
      name: '',
      id: '',
    };
  },
  isEdit: false,
});

const emit = defineEmits<Emits>();
//#endregion

//#region state
const store = useTypedStore();
const api = useApi();
const data = ref({} as PopupData);
const isVisible = ref(false);
const errors = ref<PutControlTowerCountriesTagsByIdErrors>({});
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
//#endregion

//#region methods
const open = () => {
  data.value = cloneDeep(props.popupData);
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const handleSave = () => {
  errors.value = {};
  const { id, name, country_code } = data.value;

  const promise = id
    ? api.putCountriesTags(id, { name: name.trim(), values: country_code })
    : api.postCountriesTags({ name: name.trim(), values: country_code });

  promise.then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    if(typeGuard<ErrorResponse<PutControlTowerCountriesTagsByIdErrors>>(e)){
      errors.value = e?.data?.errors ? cloneDeep(e.data.errors) : {};
    }
  });
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-currency-popup"
    :title="isEdit
      ? `${$t('settings.offersCountryGroups.confirm.editCountryGroup')}`
      : $t('settings.offersCountryGroups.confirm.addCountryGroup')"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <UiLabel
        class="t:mb-16"
        :label="$t('settings.offersCountryGroups.confirm.label')"
      />
      <UiInput
        v-model="data.name"
        class="form-input t:mb-16"
        :label="$t('settings.offersCountryGroups.confirm.name')"
        autosize
        size="big"
        :placeholder="$t('settings.offersCountryGroups.confirm.name')"
        type="text"
        :error="errors.name"
      />
      <UiSelectElement
        v-model="data.country_code"
        :placeholder="$t('settings.offersCountryGroups.confirm.country_list')"
        :label="$t('settings.offersCountryGroups.confirm.country_list')"
        :list="countries"
        filterable
        multiple
        search-param="name"
        :max-collapse-tags="5"
        collapse-tag-tooltip
        option-value-param="code"
        option-label-param="code"
        :error="errors.values"
        size="big"
      />
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
